import React, { useContext, useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";

import "./PostAmendment.css";

import { useMsal } from "@azure/msal-react";

import {
	getAPICall,
	postAPICall,
	putAPICall,
} from "../../../../config/apiCalls";

import { UserContext } from "../../../../context/UserContext";
import { generateAlphanumericCode } from "../../../../constants/constants";
import { integerRegex } from "../../../../constants/regex";
import { convertToPSTYearOnly } from "../../../../constants/dateTime";

import {
	Alert,
	Autocomplete,
	Button,
	ButtonGroup,
	CircularProgress,
	FormControl,
	InputAdornment,
	InputLabel,
	MenuItem,
	Select,
	TextField,
	Tooltip,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

const PostAmendment = () => {
	const { instance, accounts } = useMsal();
	const employeeID = useContext(UserContext);

	const [formData, setFormData] = useState({
		amendmentAmount: "",
		notes: "",
		contractType: "",
		adjustmentLocation: "",
	});
	const [numberOfServices, setNumberOfServices] = useState(1);
	const [serviceScopes, setServiceScopes] = useState({
		serviceScope1: "",
		serviceScope2: "",
		serviceScope3: "",
		serviceScope4: "",
		serviceScope5: "",
		serviceScope6: "",
		serviceScope7: "",
		serviceScope8: "",
	});
	const [serviceScopeCosts, setServiceScopeCosts] = useState({
		serviceScope1Cost: "",
		serviceScope2Cost: "",
		serviceScope3Cost: "",
		serviceScope4Cost: "",
		serviceScope5Cost: "",
		serviceScope6Cost: "",
		serviceScope7Cost: "",
		serviceScope8Cost: "",
	});

	const [hubspotDealRecord, setHubspotDealRecord] = useState(null);
	const [hubspotDealSearchValue, setHubspotDealSearchValue] = useState("");
	const [hubspotDealOptions, setHubspotDealOptions] = useState([]);

	const [propertyRecord, setPropertyRecord] = useState(null);
	const [projectRecord, setProjectRecord] = useState(null);
	const [phaseRecord, setPhaseRecord] = useState(null);

	const [allProperties, setAllProperties] = useState([]);
	const [allProjects, setAllProjects] = useState([]);
	const [allPhases, setAllPhases] = useState([]);

	const [bqePropertySearchValue, setBqePropertySearchValue] = useState("");
	const [bqePropertyRecord, setBqePropertyRecord] = useState(null);
	const [bqePropertyOptions, setBqePropertyOptions] = useState([]);
	const [bqeProjectOptions, setBqeProjectOptions] = useState([]);
	const [bqePhaseOptions, setBqePhaseOptions] = useState([]);
	const [bqeProjectID, setBqeProjectID] = useState("");
	const [bqePhaseID, setBqePhaseID] = useState("");

	const [isLoading, setIsLoading] = useState(false);
	const [isBQEPropertyLoading, setIsBQEPropertyLoading] = useState(false);
	const [isBQEProjectLoading, setIsBQEProjectLoading] = useState(false);
	const [isBQEPhaseLoading, setIsBQEPhaseLoading] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [alertMessage, setAlertMessage] = useState("");
	const [alertType, setAlertType] = useState("info");
	const [fieldErrors, setFieldErrors] = useState({});
	const [validationIssues, setValidationIssues] = useState([]);
	const isHubspotDealSelected = Boolean(
		hubspotDealRecord?.properties?.hs_object_id
	);
	const validHubSpotDealStages = new Set([
		"10201527",
		"10201530",
		"1341308700",
		"1341308701",
		"1341308702",
		"1341308703",
		"1341308704",
	]);
	const isBQESectionLoading =
		isBQEPropertyLoading || isBQEProjectLoading || isBQEPhaseLoading;

	const showContractTypeMismatch = (() => {
		if (!formData.contractType) return false;
		const expectedBqeType = formData.contractType === "fixed_fee" ? 1 : 0;
		if (formData.adjustmentLocation === "existing_phase") {
			if (!bqePhaseID) return false;
			const selectedPhase = bqePhaseOptions.find((p) => p.id === bqePhaseID);
			if (!selectedPhase) return false;
			return selectedPhase.contractType !== expectedBqeType;
		}
		if (formData.adjustmentLocation === "amendment_phase") {
			const existingAmendmentPhase = bqePhaseOptions.find((p) => p.displayName?.includes("Amendment"));
			if (!existingAmendmentPhase) return false;
			return existingAmendmentPhase.contractType !== expectedBqeType;
		}
		return false;
	})();

	const adjustmentInfoMessage = (() => {
		const ct = formData.contractType;
		const al = formData.adjustmentLocation;
		if (!ct || !al) return null;
		const basisLabel = ct === "fixed_fee" ? "a fixed fee basis" : "a time & cost basis";
		const amendmentAmt = Number(formData.amendmentAmount) || 0;
		const fmt = (n) => `$${Number(n).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
		const amtLabel = amendmentAmt ? fmt(amendmentAmt) : "Amendment amount";

		const buildAmountSuffix = (currentAmt) => {
			if (currentAmt == null || !amendmentAmt) return "";
			return ` The current amount is ${fmt(currentAmt)} and the new amount will be ${fmt(currentAmt + amendmentAmt)}.`;
		};

		if (al === "existing_phase") {
			const phase = bqePhaseOptions.find((p) => p.id === bqePhaseID);
			const phaseName = phase?.displayName || "the selected phase";
			return `${amtLabel} will be added to the existing phase ${phaseName} on ${basisLabel}.${buildAmountSuffix(phase?.contractAmount ?? null)}`;
		}
		if (al === "amendment_phase") {
			const existingAmendmentPhase = bqePhaseOptions.find((p) => p.displayName?.includes("Amendment"));
			if (existingAmendmentPhase) {
				return `${amtLabel} will be added to the existing amendment phase ${existingAmendmentPhase.displayName} on ${basisLabel}.${buildAmountSuffix(existingAmendmentPhase?.contractAmount ?? null)}`;
			}
			return `${amtLabel} will be added to a new amendment phase on ${basisLabel}.`;
		}
		return null;
	})();

	useEffect(() => {
		postAPICall(instance, accounts[0], "/api/logs/access/add", {
			page_name: "Post Amendment",
			employeeID: employeeID,
			notes: "Accessed the Post Amendment page",
		}).catch(() => {});
	}, []);

	useEffect(() => {
		fetchInitialData();
	}, []);

	const fetchInitialData = async () => {
		setIsLoading(true);

		try {
			const [propertiesRes, projectsRes, phasesRes] = await Promise.all([
				getAPICall(instance, accounts[0], "/api/jobs/property/all"),
				getAPICall(instance, accounts[0], "/api/jobs/project/all"),
				getAPICall(instance, accounts[0], "/api/jobs/phases/all"),
			]);

			setAllProperties(propertiesRes.data || []);
			setAllProjects(projectsRes.data || []);
			setAllPhases(phasesRes.data || []);
		} catch (error) {
			console.error("Error fetching post amendment form data:", error);
			setAlertMessage("Failed to load form data.");
			setAlertType("error");
		} finally {
			setIsLoading(false);
		}
	};

	const formatNameWithYear = (record) => {
		if (!record?.name) return "";
		const year = record.created ? convertToPSTYearOnly(record.created) : "";
		return year ? `${record.name} (${year})` : record.name;
	};

	const debounce = (callback, wait) => {
		let timeoutId = null;
		return (...args) => {
			window.clearTimeout(timeoutId);
			timeoutId = window.setTimeout(() => {
				callback.apply(null, args);
			}, wait);
		};
	};

	const bqePropertyNameChanged = useMemo(
		() =>
			debounce(async (event) => {
				const value = event.target.value;
				setBqePropertySearchValue(value);

				if (value.length < 3) {
					setBqePropertyOptions([]);
					return;
				}

				setIsBQEPropertyLoading(true);

				try {
					const res = await getAPICall(
						instance,
						accounts[0],
						"/api/bqe/property/byName",
						{
							propertyName: value,
						}
					);

					setBqePropertyOptions(
						res.data == ""
							? []
							: res.data.filter(
									(option) =>
										!option.displayName.endsWith("(deleted)")
							  )
					);
				} catch (error) {
					console.error("Error searching BQE properties:", error);
					setBqePropertyOptions([]);
				} finally {
					setIsBQEPropertyLoading(false);
				}
			}, 500),
		[accounts, instance]
	);

	const hubspotDealNameChanged = useMemo(
		() =>
			debounce(async (event) => {
				const value = event.target.value;
				setHubspotDealSearchValue(value);

				if (value.length < 3) {
					setHubspotDealOptions([]);
					return;
				}

				try {
					const res = await getAPICall(
						instance,
						accounts[0],
						"/api/hubspot/search/deals",
						{
							dealName: value,
						}
					);

					const filtered = (res.data ?? []).filter((deal) =>
						validHubSpotDealStages.has(
							String(deal?.properties?.dealstage ?? "")
						)
					);

					setHubspotDealOptions(filtered);
				} catch (error) {
					console.error("Error searching HubSpot deals:", error);
					setHubspotDealOptions([]);
				}
			}, 500),
		[accounts, instance]
	);

	const fetchBQEProjects = async (bqePropertyID) => {
		if (!bqePropertyID) {
			setBqeProjectOptions([]);
			setBqePhaseOptions([]);
			setBqeProjectID("");
			setBqePhaseID("");
			return [];
		}

		setIsBQEProjectLoading(true);

		try {
			const res = await getAPICall(
				instance,
				accounts[0],
				"/api/bqe/project/byProperty",
				{
					propertyID: bqePropertyID,
				}
			);

			const projectOptions =
				res.data == ""
					? []
					: res.data.filter(
							(option) =>
								!option.displayName.endsWith("(deleted)")
					  );

			setBqeProjectOptions(projectOptions);
			return projectOptions;
		} catch (error) {
			console.error("Error fetching BQE projects:", error);
			setBqeProjectOptions([]);
			return [];
		} finally {
			setIsBQEProjectLoading(false);
		}
	};

	const resetForm = () => {
		setFormData({
			amendmentAmount: "",
			notes: "",
			contractType: "",
			adjustmentLocation: "",
		});
		setNumberOfServices(1);
		setServiceScopes({
			serviceScope1: "",
			serviceScope2: "",
			serviceScope3: "",
			serviceScope4: "",
			serviceScope5: "",
			serviceScope6: "",
			serviceScope7: "",
			serviceScope8: "",
		});
		setServiceScopeCosts({
			serviceScope1Cost: "",
			serviceScope2Cost: "",
			serviceScope3Cost: "",
			serviceScope4Cost: "",
			serviceScope5Cost: "",
			serviceScope6Cost: "",
			serviceScope7Cost: "",
			serviceScope8Cost: "",
		});
		setHubspotDealRecord(null);
		setHubspotDealSearchValue("");
		setHubspotDealOptions([]);
		setPropertyRecord(null);
		setProjectRecord(null);
		setPhaseRecord(null);
		setBqePropertySearchValue("");
		setBqePropertyRecord(null);
		setBqePropertyOptions([]);
		setBqeProjectOptions([]);
		setBqePhaseOptions([]);
		setBqeProjectID("");
		setBqePhaseID("");
		setFieldErrors({});
		setValidationIssues([]);
	};

	const summarizeOutput = (
		value,
		seen = new WeakSet(),
		depth = 0,
		maxDepth = 4
	) => {
		if (value === undefined || value === null) return value ?? null;
		if (depth > maxDepth) return "[MaxDepthExceeded]";
		if (typeof value !== "object") return value;
		if (seen.has(value)) return "[Circular]";
		seen.add(value);

		if (Array.isArray(value)) {
			return value.map((v) =>
				summarizeOutput(v, seen, depth + 1, maxDepth)
			);
		}

		if ("status" in value || "data" in value || "error" in value) {
			return {
				status: value?.status ?? null,
				data: summarizeOutput(value?.data ?? null, seen, depth + 1),
				error:
					value?.error?.message ||
					value?.error ||
					value?.data?.error ||
					null,
			};
		}

		const redactedKeys = new Set([
			"authorization",
			"cookie",
			"set-cookie",
			"access_token",
			"refresh_token",
			"token",
		]);

		const summarized = {};
		Object.keys(value).forEach((key) => {
			if (redactedKeys.has(key.toLowerCase())) {
				summarized[key] = "[Redacted]";
				return;
			}
			summarized[key] = summarizeOutput(
				value[key],
				seen,
				depth + 1,
				maxDepth
			);
		});
		return summarized;
	};

	const submitPendingAmendmentFormLog = async ({
		actionType,
		actionStatus,
		errorMessage = null,
		actionClickedTime = null,
		actionCompletedTime = null,
		contractAmountPreSubmission = null,
		stepOutputs = {},
	} = {}) => {
		try {
			await postAPICall(
				instance,
				accounts[0],
				"/api/logs/pendingAmendments",
				{
					actionType,
					actionStatus,
					errorMessage,
					pendingAmendmentID: null,
					submittedByEmployeeID: employeeID ?? null,
					submittedByEmployeeName:
						accounts[0]?.name || accounts[0]?.username || null,
					actionClickedTime,
					actionCompletedTime,
					hubspotDealID: hubspotDealRecord?.properties?.hs_object_id ?? null,
					hubspotDealName: hubspotDealRecord?.properties?.dealname ?? null,
					propertyID: propertyRecord?.id ?? null,
					projectID: projectRecord?.id ?? null,
					phaseID: phaseRecord?.id ?? null,
					bqePropertyID: bqePropertyRecord?.id ?? null,
					bqeProjectID: bqeProjectID || null,
					bqePhaseID: bqePhaseID || null,
					amendmentAmount: formData.amendmentAmount || null,
					contractAmountPreSubmission,
					adjustmentAmount: formData.amendmentAmount || null,
					isSubmitted: true,
					isTimeAndMaterials: formData.contractType === "time_and_cost",
					submittedOn: actionCompletedTime,
					submittedBy: employeeID ?? null,
					selectedPropertyID: propertyRecord?.id ?? null,
					selectedProjectID: projectRecord?.id ?? null,
					selectedPhaseID: phaseRecord?.id ?? null,
					selectedBqePropertyID: bqePropertyRecord?.id ?? null,
					selectedBqeProjectID: bqeProjectID || null,
					selectedBqePhaseID: bqePhaseID || null,
					payloadSnapshot: {
						hubspotDealRecord,
						propertyRecord,
						projectRecord,
						phaseRecord,
						bqePropertyRecord,
						bqeProjectID,
						bqePhaseID,
						formData,
						numberOfServices,
						serviceScopes,
						serviceScopeCosts,
						stepOutputs: summarizeOutput(stepOutputs),
					},
					modifiedBy: employeeID ?? null,
				},
				employeeID
			);
		} catch (error) {
			console.error("Failed to submit post amendment form log:", error);
		}
	};

	const fetchBQEProjectByID = async (projectID) => {
		if (!projectID) {
			return null;
		}

		try {
			const res = await getAPICall(
				instance,
				accounts[0],
				"/api/bqe/project/byID",
				{
					id: projectID,
				}
			);

			return res.data;
		} catch (error) {
			console.error("Error fetching BQE project by ID:", error);
			return null;
		}
	};

	const fetchDealOwnerID = async (hubspotDealID) => {
		if (!hubspotDealID) {
			return null;
		}

		try {
			const res = await getAPICall(
				instance,
				accounts[0],
				"/api/hubspot/getDealFromDealID",
				{
					dealID: hubspotDealID,
				}
			);

			return res?.data?.properties?.hubspot_owner_id ?? null;
		} catch (error) {
			console.error("Error fetching HubSpot deal owner:", error);
			return null;
		}
	};

	const notifyDealOwner = async ({
		propertyID = null,
		projectID = null,
		phaseID = null,
		dealOwnerID = null,
		amendmentAmount = null,
		isTimeAndMaterials = false,
		notes = null,
		submittedViaPending = false,
	} = {}) => {
		try {
			return await postAPICall(
				instance,
				accounts[0],
				"/api/outlook/pendingAmendment/emailDealOwner",
				{
					propertyID,
					projectID,
					phaseID,
					dealOwnerID,
					amendmentAmount,
					isTimeAndMaterials,
					notes,
					submittedViaPending,
				},
				employeeID
			);
		} catch (error) {
			console.error("Error notifying deal owner:", error);
			return null;
		}
	};

	const fetchBQEProperty = async (bqeID) => {
		if (!bqeID) {
			setBqePropertyRecord(null);
			setBqePropertySearchValue("");
			return null;
		}

		try {
			const res = await getAPICall(
				instance,
				accounts[0],
				"/api/bqe/property/byID",
				{
					id: bqeID,
				}
			);

			setBqePropertyRecord(res.data);
			setBqePropertySearchValue(res.data?.displayName ?? "");
			return res.data;
		} catch (error) {
			console.error("Error fetching BQE property:", error);
			setBqePropertyRecord(null);
			setBqePropertySearchValue("");
			return null;
		}
	};

	const fetchBQEPhases = async (projectID) => {
		if (!projectID) {
			setBqePhaseOptions([]);
			setBqePhaseID("");
			return;
		}

		setIsBQEPhaseLoading(true);

		try {
			const res = await getAPICall(
				instance,
				accounts[0],
				"/api/bqe/phases/byProject",
				{
					projectID,
				}
			);

			setBqePhaseOptions(
				res.data == ""
					? []
					: res.data.filter(
							(option) =>
								!option.displayName.endsWith("(deleted)")
					  )
			);
		} catch (error) {
			console.error("Error fetching BQE phases:", error);
			setBqePhaseOptions([]);
		} finally {
			setIsBQEPhaseLoading(false);
		}
	};

	const fetchAmendmentPhase = async (projectID) => {
		if (!projectID) {
			return null;
		}

		try {
			const res = await getAPICall(
				instance,
				accounts[0],
				"/api/bqe/phases/byProject",
				{
					projectID,
				}
			);

			if (res.data == "") {
				return null;
			}

			const undeletedPhases = res.data.filter(
				(option) => !option.displayName.endsWith("(deleted)")
			);

			return (
				undeletedPhases.find((phase) =>
					phase.displayName.includes("Amendment")
				) || null
			);
		} catch (error) {
			console.error("Error fetching amendment phase:", error);
			return null;
		}
	};

	const resetResolvedFields = () => {
		setPropertyRecord(null);
		setProjectRecord(null);
		setPhaseRecord(null);
		setBqePropertyRecord(null);
		setBqePropertySearchValue("");
		setBqePropertyOptions([]);
		setBqeProjectOptions([]);
		setBqePhaseOptions([]);
		setBqeProjectID("");
		setBqePhaseID("");
	};

	const hydrateSelectionsFromProject = async (project) => {
		if (!project) {
			return;
		}

		const matchedProperty =
			allProperties.find((property) => property.id === project.property_id) ||
			null;

		setPropertyRecord(matchedProperty);
		setProjectRecord(project);
		setPhaseRecord(null);

		const propertyBqeID = matchedProperty?.bqe_id || null;
		const projectBqeID = project?.bqe_id || "";

		await fetchBQEProperty(propertyBqeID);
		const bqeProjects = await fetchBQEProjects(propertyBqeID);

		if (projectBqeID) {
			const hasStoredProject = bqeProjects.some(
				(option) => option.id === projectBqeID
			);

			if (!hasStoredProject) {
				const fallbackProject = await fetchBQEProjectByID(projectBqeID);

				if (fallbackProject) {
					setBqeProjectOptions((prev) => {
						if (prev.some((option) => option.id === fallbackProject.id)) {
							return prev;
						}
						return [...prev, fallbackProject];
					});
				}
			}
		}

		setBqeProjectID(projectBqeID || "");

		const autofilledFields = [];
		if (matchedProperty) autofilledFields.push("property");
		if (project) autofilledFields.push("project");
		if (propertyBqeID) autofilledFields.push("bqeProperty");
		if (projectBqeID) autofilledFields.push("bqeProject");

		if (projectBqeID) {
			await fetchBQEPhases(projectBqeID);
			setBqePhaseID("");
		} else {
			setBqePhaseID("");
		}

		clearFieldErrors(autofilledFields);
	};

	const handleFormChange = (event) => {
		const { name, value, checked, type } = event.target;
		const fieldNameMap = {
			amendmentAmount: "amendmentAmount",
			notes: "notes",
			contractType: "contractType",
		};

		if (name === "amendmentAmount") {
			const digitsOnly = value.replace(/\D/g, "");
			setFormData((prev) => ({
				...prev,
				amendmentAmount: digitsOnly,
			}));
			clearFieldError("amendmentAmount");
			return;
		}

		setFormData((prev) => ({
			...prev,
			[name]: type === "checkbox" ? checked : value,
		}));
		if (fieldNameMap[name]) {
			clearFieldError(fieldNameMap[name]);
		}
	};

	const updateBQEPhaseAmount = async (existingAmendmentPhase) => {
		try {
			const updatedAmount =
				existingAmendmentPhase.contractAmount +
				Number(formData.amendmentAmount);

			const updateRes = await putAPICall(
				instance,
				accounts[0],
				"/api/bqe/project/update/amount",
				{
					projectId: existingAmendmentPhase.id,
					contractAmount: updatedAmount,
					version: existingAmendmentPhase.version,
				}
			);

			return updateRes.status === 200;
		} catch (error) {
			console.error("Error updating BQE Phase amount:", error);
			return false;
		}
	};

	const associateOICCustomField = async (projectID, oic) => {
		try {
			let bqeOICValue = null;

			if (oic === "WA") {
				bqeOICValue = "d5e226fe-5798-43a4-a752-7bcd087eeda3";
			} else if (oic === "OR") {
				bqeOICValue = "03356b2a-8467-46ef-9ce7-cf4dc99599d3";
			} else if (oic === "UT") {
				bqeOICValue = "60d08179-1541-41ee-938f-9415ba4b5872";
			} else {
				bqeOICValue = "d5e226fe-5798-43a4-a752-7bcd087eeda3";
			}

			await postAPICall(instance, accounts[0], "/api/bqe/oic/assign", {
				bqeProjectId: projectID,
				bqeOICValue,
			});
		} catch (err) {
			console.error("Error assigning OIC custom field:", err);
		}
	};

	const createNewBQEPhase = async () => {
		try {
			const bqeProject = bqeProjectOptions.find(
				(project) => project.id === bqeProjectID
			);

			if (!bqeProject) {
				setAlertMessage("Please select a valid BQE Project.");
				setAlertType("error");
				return false;
			}

			const creationRes = await postAPICall(
				instance,
				accounts[0],
				"/api/bqe/phase/create",
				{
					clientId: bqeProject.clientId,
					phaseCode: `PH-${generateAlphanumericCode()}`,
					managerId: bqeProject.managerId,
					contractType: formData.contractType === "fixed_fee" ? 1 : 0,
					phaseName: "Amendment",
					addressLine1: propertyRecord?.address_line1,
					addressLine2: propertyRecord?.address_line2,
					city: propertyRecord?.city,
					state: propertyRecord?.state,
					zip: propertyRecord?.zip,
					oic: propertyRecord?.oic,
					classId: bqeProject.classId,
					parentId: bqeProject.id,
					rootId: bqeProject.rootProjectId,
					contractAmount: formData.amendmentAmount,
				}
			);

			if (creationRes.status !== 200) {
				return false;
			}

			if (creationRes.data?.id) {
				await associateOICCustomField(
					creationRes.data.id,
					propertyRecord?.oic
				);
			}

			return true;
		} catch (error) {
			console.error("Error creating BQE Phase amount:", error);
			return false;
		}
	};

	const addServicePressed = () => {
		if (numberOfServices === 8) {
			return;
		}

		setNumberOfServices((prevState) => prevState + 1);
	};

	const removeServicePressed = () => {
		if (numberOfServices === 1) {
			return;
		}

		setNumberOfServices((prevState) => prevState - 1);
	};

	const serviceScopeChanged = (event) => {
		clearFieldError(event.target.name);
		setServiceScopes((prevState) => ({
			...prevState,
			[event.target.name]: event.target.value,
		}));
	};

	const serviceScopeCostChanged = (event) => {
		const digitsOnly = event.target.value.replace(/\D/g, "");
		clearFieldError(event.target.name);
		setServiceScopeCosts((prevState) => ({
			...prevState,
			[event.target.name]: digitsOnly,
		}));
	};

	const clearFieldError = (fieldName) => {
		setFieldErrors((prev) => {
			if (!prev[fieldName]) {
				return prev;
			}

			const next = { ...prev };
			delete next[fieldName];
			return next;
		});

		setValidationIssues((prevIssues) =>
			prevIssues.filter((issue) => issue.field !== fieldName)
		);
	};

	const clearFieldErrors = (fieldNames) => {
		if (!fieldNames?.length) {
			return;
		}

		const fieldsToClear = new Set(fieldNames);

		setFieldErrors((prev) => {
			const next = { ...prev };
			fieldsToClear.forEach((fieldName) => {
				delete next[fieldName];
			});
			return next;
		});

		setValidationIssues((prevIssues) => {
			const remainingIssues = prevIssues.filter(
				(issue) => !fieldsToClear.has(issue.field)
			);

			if (
				remainingIssues.length === 0 &&
				alertType === "error" &&
				alertMessage === "Please complete all required fields."
			) {
				setAlertMessage("");
			}

			return remainingIssues;
		});
	};

	const formatWholeDollarAmount = (value) => {
		if (!value) {
			return "";
		}

		const digitsOnly = String(value).replace(/\D/g, "");
		if (!digitsOnly) {
			return "";
		}

		return Number(digitsOnly).toLocaleString("en-US");
	};

	const handleHubspotDealChange = (event, newValue) => {
		if (!newValue?.properties) {
			setHubspotDealRecord(null);
			setHubspotDealSearchValue("");
			setHubspotDealOptions([]);
			resetResolvedFields();
			return;
		}

		clearFieldError("hubspotDeal");
		setHubspotDealRecord(newValue);
		setHubspotDealSearchValue(newValue.properties.dealname ?? "");

		const matchedProject =
			allProjects.find(
				(project) =>
					String(project.hubspot_id) ===
					String(newValue.properties.hs_object_id)
			) || null;

		if (matchedProject) {
			hydrateSelectionsFromProject(matchedProject);
		} else {
			resetResolvedFields();
		}
	};

	const handlePropertyChange = (event, newValue) => {
		clearFieldError("property");
		setPropertyRecord(newValue);
		setProjectRecord(null);
		setPhaseRecord(null);
		setBqeProjectID("");
		setBqePhaseID("");
		setBqeProjectOptions([]);
		setBqePhaseOptions([]);

		if (newValue?.bqe_id) {
			fetchBQEProperty(newValue.bqe_id);
			fetchBQEProjects(newValue.bqe_id);
		} else {
			setBqePropertyRecord(null);
			setBqePropertySearchValue("");
		}
	};

	const handleProjectChange = (event, newValue) => {
		clearFieldError("project");
		setProjectRecord(newValue);
		setPhaseRecord(null);
		setBqePhaseID("");

		if (newValue?.bqe_id) {
			setBqeProjectID(newValue.bqe_id);
			fetchBQEPhases(newValue.bqe_id).then(() => {
				fetchAmendmentPhase(newValue.bqe_id).then((amendmentPhase) => {
					setBqePhaseID(amendmentPhase?.id || "");
				});
			});
		} else {
			setBqeProjectID("");
			setBqePhaseOptions([]);
		}
	};

	const handlePhaseChange = (event, newValue) => {
		clearFieldError("phase");
		setPhaseRecord(newValue);
	};

	const bqePropertySelected = (event, newValue) => {
		if (!newValue?.id) {
			setBqePropertyRecord(null);
			setBqePropertySearchValue("");
			setBqeProjectID("");
			setBqePhaseID("");
			setBqeProjectOptions([]);
			setBqePhaseOptions([]);
			return;
		}

		clearFieldError("bqeProperty");
		setBqePropertyRecord(newValue);
		setBqePropertySearchValue(newValue.displayName);
		setBqeProjectID("");
		setBqePhaseID("");
		fetchBQEProjects(newValue.id);
	};

	const handleAdjustmentLocationChange = (event) => {
		clearFieldError("adjustmentLocation");
		setFormData((prev) => ({ ...prev, adjustmentLocation: event.target.value }));
	};

	const bqeProjectSelected = (event) => {
		const selectedProjectID = event.target.value;
		clearFieldError("bqeProject");
		setBqeProjectID(selectedProjectID);
		setBqePhaseID("");
		fetchBQEPhases(selectedProjectID);
	};

	const bqePhaseSelected = (event) => {
		clearFieldError("bqePhase");
		setBqePhaseID(event.target.value);
	};

	const validateInput = () => {
		const nextFieldErrors = {};
		const nextValidationIssues = [];

		const addIssue = (field, message) => {
			nextFieldErrors[field] = message;
			nextValidationIssues.push({ field, message });
		};

		if (!hubspotDealRecord?.properties?.hs_object_id) {
			addIssue("hubspotDeal", "Please select a HubSpot deal.");
		}

		if (!propertyRecord?.id) {
			addIssue("property", "Please select a property.");
		}

		if (!projectRecord?.id) {
			addIssue("project", "Please select a project.");
		}

		if (!phaseRecord?.id) {
			addIssue("phase", "Please select a phase.");
		}

		if (formData.amendmentAmount === "") {
			addIssue(
				"amendmentAmount",
				"Please enter an amendment amount."
			);
		} else if (!integerRegex.test(formData.amendmentAmount)) {
			addIssue(
				"amendmentAmount",
				"Please enter a non-negative whole dollar amount."
			);
		}

		if (!bqePropertyRecord?.id) {
			addIssue("bqeProperty", "Please select a BQE property.");
		}

		if (!bqeProjectID) {
			addIssue("bqeProject", "Please select a BQE project.");
		}

		if (!bqePhaseID) {
			addIssue("bqePhase", "Please select a BQE phase.");
		}

		if (!formData.contractType) {
			addIssue("contractType", "Please select a contract type.");
		}

		if (!formData.adjustmentLocation) {
			addIssue("adjustmentLocation", "Please select an adjustment location.");
		}

		if (formData.contractType !== "time_and_cost") {
			for (let i = 1; i <= numberOfServices; i++) {
				const scopeField = `serviceScope${i}`;
				const costField = `serviceScope${i}Cost`;
				const scopeValue = serviceScopes[scopeField]?.trim() || "";
				const costValue = serviceScopeCosts[costField]?.trim() || "";

				if (!scopeValue) {
					addIssue(
						scopeField,
						i === 1
							? "Please enter at least one line item description."
							: `Please enter a description for line item ${i}.`
					);
				}

				if (!costValue) {
					addIssue(
						costField,
						i === 1
							? "Please enter a cost for the first line item."
							: `Please enter a cost for line item ${i}.`
					);
				}
			}
		}

		setFieldErrors(nextFieldErrors);
		setValidationIssues(nextValidationIssues);

		if (nextValidationIssues.length > 0) {
			setAlertMessage("Please complete all required fields.");
			setAlertType("error");
			return false;
		}

		return true;
	};

	const getNextAmendmentNumber = () => {
		const currentAmendmentNumber = parseInt(
			hubspotDealRecord?.properties?.current_amendment_number,
			10
		);

		return Number.isNaN(currentAmendmentNumber)
			? 1
			: currentAmendmentNumber + 1;
	};

	const serviceRows = Array.from({ length: numberOfServices }, (_, index) => {
		const rowNumber = index + 1;
		return {
			rowNumber,
			scopeKey: `serviceScope${rowNumber}`,
			costKey: `serviceScope${rowNumber}Cost`,
		};
	});

	const buildMostRecentAmendmentNotes = () => {
		const lineItems = serviceRows
			.map(({ rowNumber, scopeKey, costKey }) => {
				const service = serviceScopes[scopeKey]?.trim() || "";
				const cost = serviceScopeCosts[costKey]?.trim() || "";

				if (!service && !cost) {
					return null;
				}

				return `Line Item ${rowNumber}: ${service} ($${cost})`;
			})
			.filter(Boolean)
			.join(" | ");

		return [
			`Deal: ${hubspotDealRecord?.properties?.dealname || ""}`,
			`Property: ${propertyRecord?.name || ""}`,
			`Project: ${projectRecord?.display_name || projectRecord?.name || ""}`,
			`Phase: ${phaseRecord?.name || ""}`,
			`Amendment Amount: $${formData.amendmentAmount || ""}`,
			`Contract Type: ${
				formData.contractType === "time_and_cost" ? "Time & Cost" : "Fixed Fee"
			}`,
			lineItems,
			formData.notes?.trim()
				? `Notes: ${formData.notes.trim()}`
				: null,
		]
			.filter(Boolean)
			.join(" | ");
	};

	const getContractAmountPreSubmission = ({
		targetPhase = null,
		isCreatingNewPhase = false,
	} = {}) => {
		if (isCreatingNewPhase) {
			return 0;
		}

		return targetPhase?.contractAmount ?? 0;
	};

	const handleSubmit = async () => {
		if (!validateInput()) {
			return;
		}

		const isTimeAndMaterials = formData.contractType === "time_and_cost";
		const actionType = "submit_post";
		const actionClickedTime = new Date().toISOString();
		const stepOutputs = {};

		setIsSubmitting(true);
		setAlertMessage("Submitting post amendment...");
		setAlertType("info");

		try {
			const amendmentNumber = getNextAmendmentNumber();
			const mostRecentAmendmentNotes = buildMostRecentAmendmentNotes();
			const dealName = hubspotDealRecord?.properties?.dealname || "";
			const primaryTaskOwnerID =
				hubspotDealRecord?.properties?.oic === "OR"
					? "84145049"
					: "57152858";
			const taskOwnerIDs =
				primaryTaskOwnerID === "57152858"
					? [primaryTaskOwnerID, "64990791"]
					: [primaryTaskOwnerID];
			let contractAmountPreSubmission = null;

			if (formData.adjustmentLocation === "existing_phase") {
				const targetPhase = bqePhaseOptions.find((p) => p.id === bqePhaseID);
				contractAmountPreSubmission = getContractAmountPreSubmission({ targetPhase });

				const updateSuccess = await updateBQEPhaseAmount(targetPhase);
				stepOutputs.updateBQEPhaseAmount = summarizeOutput(updateSuccess);

				if (!updateSuccess) {
					await submitPendingAmendmentFormLog({
						actionType,
						actionStatus: "failure",
						errorMessage: "Failed to update the BQE Phase.",
						actionClickedTime,
						actionCompletedTime: new Date().toISOString(),
						contractAmountPreSubmission,
						stepOutputs,
					});
					throw new Error("Failed to update the BQE Phase.");
				}
			} else {
				const existingAmendmentPhase = bqePhaseOptions.find((p) => p.displayName?.includes("Amendment"));

				if (existingAmendmentPhase) {
					contractAmountPreSubmission = getContractAmountPreSubmission({ targetPhase: existingAmendmentPhase });

					const updateSuccess = await updateBQEPhaseAmount(existingAmendmentPhase);
					stepOutputs.updateBQEPhaseAmount = summarizeOutput(updateSuccess);

					if (!updateSuccess) {
						await submitPendingAmendmentFormLog({
							actionType,
							actionStatus: "failure",
							errorMessage: "Failed to update the BQE Phase.",
							actionClickedTime,
							actionCompletedTime: new Date().toISOString(),
							contractAmountPreSubmission,
							stepOutputs,
						});
						throw new Error("Failed to update the BQE Phase.");
					}
				} else {
					contractAmountPreSubmission = getContractAmountPreSubmission({ isCreatingNewPhase: true });

					const creationSuccess = await createNewBQEPhase();
					stepOutputs.createNewBQEPhase = summarizeOutput(creationSuccess);

					if (!creationSuccess) {
						await submitPendingAmendmentFormLog({
							actionType,
							actionStatus: "failure",
							errorMessage: "Failed to create the BQE Phase.",
							actionClickedTime,
							actionCompletedTime: new Date().toISOString(),
							contractAmountPreSubmission,
							stepOutputs,
						});
						throw new Error("Failed to create the BQE Phase.");
					}
				}
			}

			const hubspotRes = await postAPICall(
				instance,
				accounts[0],
				"/api/hubspot/updateAmendmentCount",
				{
					dealID: hubspotDealRecord?.properties?.hs_object_id ?? null,
					amendmentNumber,
					mostRecentAmendmentNotes,
					incomingAmendmentIsFromPostAmendments: true,
				},
				employeeID
			);
			stepOutputs.updateAmendmentCount = summarizeOutput(hubspotRes);

			if (hubspotRes.status !== 200) {
				await submitPendingAmendmentFormLog({
					actionType,
					actionStatus: "failure",
					errorMessage:
						hubspotRes?.data?.message ||
						hubspotRes?.data?.error ||
						"Failed to update HubSpot amendment count.",
					actionClickedTime,
					actionCompletedTime: new Date().toISOString(),
					contractAmountPreSubmission,
					stepOutputs,
				});
				throw new Error(
					hubspotRes?.data?.message ||
						hubspotRes?.data?.error ||
						"Failed to update HubSpot amendment count."
				);
			}

			const hubspotLineItemRes = await postAPICall(
				instance,
				accounts[0],
				"/api/hubspot/addAmendmentLineItems",
				{
					dealID: hubspotDealRecord?.properties?.hs_object_id ?? null,
					amendmentNumber,
					lineItem1: serviceScopes.serviceScope1?.trim() || "",
					lineItem2: serviceScopes.serviceScope2?.trim() || "",
					lineItem3: serviceScopes.serviceScope3?.trim() || "",
					lineItem4: serviceScopes.serviceScope4?.trim() || "",
					lineItem5: serviceScopes.serviceScope5?.trim() || "",
					lineItem6: serviceScopes.serviceScope6?.trim() || "",
					lineItem7: serviceScopes.serviceScope7?.trim() || "",
					lineItem8: serviceScopes.serviceScope8?.trim() || "",
					lineItem1Cost: serviceScopeCosts.serviceScope1Cost
						? parseInt(serviceScopeCosts.serviceScope1Cost, 10)
						: 0,
					lineItem2Cost: serviceScopeCosts.serviceScope2Cost
						? parseInt(serviceScopeCosts.serviceScope2Cost, 10)
						: 0,
					lineItem3Cost: serviceScopeCosts.serviceScope3Cost
						? parseInt(serviceScopeCosts.serviceScope3Cost, 10)
						: 0,
					lineItem4Cost: serviceScopeCosts.serviceScope4Cost
						? parseInt(serviceScopeCosts.serviceScope4Cost, 10)
						: 0,
					lineItem5Cost: serviceScopeCosts.serviceScope5Cost
						? parseInt(serviceScopeCosts.serviceScope5Cost, 10)
						: 0,
					lineItem6Cost: serviceScopeCosts.serviceScope6Cost
						? parseInt(serviceScopeCosts.serviceScope6Cost, 10)
						: 0,
					lineItem7Cost: serviceScopeCosts.serviceScope7Cost
						? parseInt(serviceScopeCosts.serviceScope7Cost, 10)
						: 0,
					lineItem8Cost: serviceScopeCosts.serviceScope8Cost
						? parseInt(serviceScopeCosts.serviceScope8Cost, 10)
						: 0,
				},
				employeeID
			);
			stepOutputs.addAmendmentLineItems = summarizeOutput(hubspotLineItemRes);

			if (hubspotLineItemRes.status !== 200) {
				await submitPendingAmendmentFormLog({
					actionType,
					actionStatus: "failure",
					errorMessage:
						hubspotLineItemRes?.data?.message ||
						hubspotLineItemRes?.data?.error ||
						"Failed to add HubSpot line items.",
					actionClickedTime,
					actionCompletedTime: new Date().toISOString(),
					contractAmountPreSubmission,
					stepOutputs,
				});
				throw new Error(
					hubspotLineItemRes?.data?.message ||
						hubspotLineItemRes?.data?.error ||
						"Failed to add HubSpot line items."
				);
			}

			const hubspotTaskRes = await postAPICall(
				instance,
				accounts[0],
				"/api/hubspot/createAmendmentTask",
				{
					dealID: hubspotDealRecord?.properties?.hs_object_id ?? null,
					taskCreatedTime: dayjs().startOf("day").valueOf(),
					taskSubject: `Post Amendment Form Submitted: ${dealName}`,
					taskBody: `${mostRecentAmendmentNotes}\nPlease manually switch the deal stage to reflect the number amendments on this deal.`,
					taskOwnerIDs,
				},
				employeeID
			);
			stepOutputs.createAmendmentTask = summarizeOutput(hubspotTaskRes);

			if (hubspotTaskRes.status !== 200) {
				await submitPendingAmendmentFormLog({
					actionType,
					actionStatus: "failure",
					errorMessage:
						hubspotTaskRes?.data?.message ||
						hubspotTaskRes?.data?.error ||
						"Failed to create HubSpot amendment task.",
					actionClickedTime,
					actionCompletedTime: new Date().toISOString(),
					contractAmountPreSubmission,
					stepOutputs,
				});
				throw new Error(
					hubspotTaskRes?.data?.message ||
						hubspotTaskRes?.data?.error ||
						"Failed to create HubSpot amendment task."
				);
			}

			const dealOwnerID =
				hubspotDealRecord?.properties?.hubspot_owner_id ||
				(await fetchDealOwnerID(
					hubspotDealRecord?.properties?.hs_object_id ?? null
				));
			stepOutputs.hubspotDealOwnerID = dealOwnerID;

			if (!dealOwnerID) {
				await submitPendingAmendmentFormLog({
					actionType,
					actionStatus: "failure",
					errorMessage: "Failed to resolve HubSpot deal owner.",
					actionClickedTime,
					actionCompletedTime: new Date().toISOString(),
					contractAmountPreSubmission,
					stepOutputs,
				});
				throw new Error("Failed to resolve HubSpot deal owner.");
			}

			const dealOwnerNotificationRes = await notifyDealOwner({
				propertyID: propertyRecord?.id ?? null,
				projectID: projectRecord?.id ?? null,
				phaseID: phaseRecord?.id ?? null,
				dealOwnerID,
				amendmentAmount: formData.amendmentAmount || null,
				isTimeAndMaterials,
				notes: formData.notes.trim() || null,
				submittedViaPending: false,
			});
			stepOutputs.dealOwnerNotification =
				summarizeOutput(dealOwnerNotificationRes);

			if (
				!dealOwnerNotificationRes ||
				dealOwnerNotificationRes?.data?.error
			) {
				await submitPendingAmendmentFormLog({
					actionType,
					actionStatus: "failure",
					errorMessage:
						dealOwnerNotificationRes?.data?.error ||
						"Failed to notify deal owner.",
					actionClickedTime,
					actionCompletedTime: new Date().toISOString(),
					contractAmountPreSubmission,
					stepOutputs,
				});
				throw new Error(
					dealOwnerNotificationRes?.data?.error ||
						"Failed to notify deal owner."
				);
			}

			const res = await postAPICall(
				instance,
				accounts[0],
				"/api/jobs/pending/amendments/create",
				{
					hubspotDealID:
						hubspotDealRecord?.properties?.hs_object_id ?? null,
					hubspotDealName:
						hubspotDealRecord?.properties?.dealname ?? null,
					propertyID: propertyRecord?.id ?? null,
					projectID: projectRecord?.id ?? null,
					phaseID: phaseRecord?.id ?? null,
					bqePropertyID: bqePropertyRecord?.id ?? null,
					bqeProjectID: bqeProjectID || null,
					bqePhaseID: bqePhaseID || null,
					amendmentAmount: formData.amendmentAmount,
					notes: formData.notes.trim() || null,
					contractAmountPreSubmission,
					submittedByEmployeeID: employeeID || null,
					submittedByEmployeeName:
						accounts[0]?.name || accounts[0]?.username || null,
					isTimeAndMaterials,
					adjustmentLocation: formData.adjustmentLocation || null,
				},
				employeeID
			);
			stepOutputs.createPendingAmendmentRecord = summarizeOutput(res);

			if (res.status !== 200) {
				await submitPendingAmendmentFormLog({
					actionType,
					actionStatus: "failure",
					errorMessage: "Failed to submit post amendment.",
					actionClickedTime,
					actionCompletedTime: new Date().toISOString(),
					contractAmountPreSubmission,
					stepOutputs,
				});
				throw new Error("Failed to submit post amendment.");
			}

			await submitPendingAmendmentFormLog({
				actionType,
				actionStatus: "success",
				actionClickedTime,
				actionCompletedTime: new Date().toISOString(),
				contractAmountPreSubmission,
				stepOutputs,
			});

			setAlertMessage("Post amendment submitted successfully.");
			setAlertType("success");
			resetForm();
		} catch (error) {
			console.error("Error submitting post amendment:", error);
			setAlertMessage(
				error?.message || "Failed to submit post amendment."
			);
			setAlertType("error");
			setIsSubmitting(false);
			return;
		}

		setIsSubmitting(false);
	};

	return (
		<div className="PostAmendment">
			<div className="postAmendmentHeader">
				<div className="postAmendmentTitle">Post Amendments</div>
				<div className="postAmendmentHeaderInfo">
					<div className="postAmendmentHeaderInfoIcon">
						<InfoOutlinedIcon fontSize="small" />
					</div>
					<div className="postAmendmentHeaderInfoText">
						<div>
							Post Amendments should only be submitted for email or
							written approvals of amendments.
						</div>
					</div>
				</div>
			</div>
			{isLoading && (
				<div className="postAmendmentLoading">
					<CircularProgress />
				</div>
			)}
			{!isLoading && (
				<div className="postAmendmentContainer">
					<div className="postAmendmentCardColumn">
						<div className="postAmendmentCard">
						<div className="postAmendmentCardHeader">
							General Information
						</div>
							<div className="postAmendmentCardRow">
								<Autocomplete
									sx={{ flex: 1 }}
									options={hubspotDealOptions}
									getOptionLabel={(option) =>
										option?.properties?.dealname ?? ""
									}
									filterSelectedOptions
									value={hubspotDealRecord}
									inputValue={hubspotDealSearchValue}
									onChange={handleHubspotDealChange}
									onInputChange={(event, newValue, reason) => {
										if (reason !== "reset") {
											setHubspotDealSearchValue(newValue);
										}
									}}
									clearOnBlur={false}
									renderInput={(params) => (
										<TextField
											{...params}
											label="HubSpot Deal"
											placeholder="HubSpot Deal"
											onKeyUp={hubspotDealNameChanged}
											error={Boolean(fieldErrors.hubspotDeal)}
											helperText={fieldErrors.hubspotDeal || ""}
										/>
									)}
								/>
							</div>
							<div className="postAmendmentGeneralInfoColumn">
								<Autocomplete
									sx={{ flex: 1 }}
									options={allProperties}
									getOptionLabel={(option) => option?.name || ""}
									value={propertyRecord}
									onChange={handlePropertyChange}
									disabled={!isHubspotDealSelected}
									renderInput={(params) => (
										<TextField
											{...params}
											label="Property"
											error={Boolean(fieldErrors.property)}
											helperText={fieldErrors.property || ""}
										/>
									)}
								/>
							</div>
							<div className="postAmendmentGeneralInfoColumn">
								<Autocomplete
									sx={{ flex: 1 }}
									options={allProjects.filter(
										(project) =>
											project.property_id === propertyRecord?.id
									)}
									getOptionLabel={formatNameWithYear}
									value={projectRecord}
									onChange={handleProjectChange}
									disabled={
										!isHubspotDealSelected || !propertyRecord
									}
									renderInput={(params) => (
										<TextField
											{...params}
											label="Project"
											error={Boolean(fieldErrors.project)}
											helperText={fieldErrors.project || ""}
										/>
									)}
								/>
							</div>
							<div className="postAmendmentGeneralInfoColumn">
								<Autocomplete
									sx={{ flex: 1 }}
									options={allPhases.filter(
										(phase) =>
											phase.property_id === propertyRecord?.id &&
											phase.project_id === projectRecord?.id
									)}
									getOptionLabel={formatNameWithYear}
									value={phaseRecord}
									onChange={handlePhaseChange}
									disabled={
										!isHubspotDealSelected ||
										!propertyRecord ||
										!projectRecord
									}
									renderInput={(params) => (
										<TextField
											{...params}
											label="Phase"
											error={Boolean(fieldErrors.phase)}
											helperText={fieldErrors.phase || ""}
										/>
									)}
								/>
							</div>
							<div className="postAmendmentCardRow">
								<TextField
									sx={{ flex: 1 }}
									name="amendmentAmount"
									label="Amendment Amount"
									value={formatWholeDollarAmount(
										formData.amendmentAmount
									)}
									onChange={handleFormChange}
									disabled={!isHubspotDealSelected}
									error={Boolean(fieldErrors.amendmentAmount)}
									helperText={fieldErrors.amendmentAmount || ""}
									InputProps={{
										startAdornment: (
											<InputAdornment position="start">
												$
											</InputAdornment>
										),
										endAdornment: (
											<InputAdornment position="end">
												<Tooltip title="If amount is a range, put MAX amount">
													<InfoOutlinedIcon
														fontSize="small"
														sx={{ color: "#6b7280", cursor: "help" }}
													/>
												</Tooltip>
											</InputAdornment>
										),
									}}
								/>
							</div>
						<div className="postAmendmentCardRow">
							<TextField
								sx={{ flex: 1 }}
								name="notes"
								label="Notes/Email Text"
								value={formData.notes}
								onChange={handleFormChange}
								disabled={!isHubspotDealSelected}
								multiline
								minRows={4}
							/>
						</div>
					</div>
					</div>
					<div className="postAmendmentCardColumn">
						<div className="postAmendmentCard">
							<div className="postAmendmentCardHeader">
								<span>BQE Fields</span>
								{isBQESectionLoading && (
									<CircularProgress size={20} thickness={5} />
								)}
							</div>
							<div className="postAmendmentCardRow">
								<Autocomplete
									sx={{ flex: 1 }}
									options={bqePropertyOptions}
									getOptionLabel={(option) =>
										option?.displayName ?? ""
									}
									filterSelectedOptions
									value={bqePropertyRecord}
									inputValue={bqePropertySearchValue}
									onChange={bqePropertySelected}
									onInputChange={(event, newValue, reason) => {
										if (reason !== "reset") {
											setBqePropertySearchValue(newValue);
										}
									}}
									clearOnBlur={false}
									loading={isBQEPropertyLoading}
									disabled={!isHubspotDealSelected}
									renderInput={(params) => (
										<TextField
											{...params}
											label="BQE Property"
											placeholder="BQE Property"
											onKeyUp={bqePropertyNameChanged}
											error={Boolean(fieldErrors.bqeProperty)}
											helperText={fieldErrors.bqeProperty || ""}
										/>
									)}
								/>
							</div>
							<div className="postAmendmentCardRow">
								<FormControl
									sx={{ flex: 1 }}
									error={Boolean(fieldErrors.bqeProject)}
								>
									<InputLabel id="postAmendmentBqeProjectLabel">
										BQE Project
									</InputLabel>
									<Select
										labelId="postAmendmentBqeProjectLabel"
										value={bqeProjectID}
										label="BQE Project"
										onChange={bqeProjectSelected}
										disabled={!isHubspotDealSelected || !bqePropertyRecord}
									>
										{bqeProjectOptions.map((option) => (
											<MenuItem key={option.id} value={option.id}>
												{option.displayName}
											</MenuItem>
										))}
									</Select>
									{fieldErrors.bqeProject && (
										<div className="postAmendmentFieldError">
											{fieldErrors.bqeProject}
										</div>
									)}
								</FormControl>
							</div>
							<div className="postAmendmentCardRow">
								<FormControl
									sx={{ flex: 1 }}
									error={Boolean(fieldErrors.bqePhase)}
								>
									<InputLabel id="postAmendmentBqePhaseLabel">
										BQE Phase
									</InputLabel>
									<Select
										labelId="postAmendmentBqePhaseLabel"
										value={bqePhaseID}
										label="BQE Phase"
										onChange={bqePhaseSelected}
										disabled={!isHubspotDealSelected || !bqeProjectID}
										endAdornment={
											<InputAdornment position="end" sx={{ mr: 3 }}>
												<Tooltip title="Choose the phase associated with the amendment, this is NOT where the adjustment amount is going unless you select 'Existing Phase' for Adjustment Location">
													<InfoOutlinedIcon
														fontSize="small"
														sx={{ color: "#6b7280", cursor: "help" }}
													/>
												</Tooltip>
											</InputAdornment>
										}
									>
										{bqePhaseOptions.filter((option) => !option.displayName?.includes("Amendment")).map((option) => (
											<MenuItem key={option.id} value={option.id}>
												{option.displayName}
											</MenuItem>
										))}
									</Select>
									{fieldErrors.bqePhase && (
										<div className="postAmendmentFieldError">
											{fieldErrors.bqePhase}
										</div>
									)}
								</FormControl>
							</div>
							<div className="postAmendmentCardRow" style={{ gap: 16 }}>
								<FormControl sx={{ flex: 1 }} disabled={!isHubspotDealSelected} error={Boolean(fieldErrors.contractType)}>
									<InputLabel id="postAmendmentContractTypeLabel">Contract Type</InputLabel>
									<Select
										labelId="postAmendmentContractTypeLabel"
										name="contractType"
										value={formData.contractType}
										label="Contract Type"
										onChange={handleFormChange}
									>
										<MenuItem value="fixed_fee">Fixed Fee</MenuItem>
										<MenuItem value="time_and_cost">Time &amp; Cost</MenuItem>
									</Select>
									{fieldErrors.contractType && (
										<div className="postAmendmentFieldError">
											{fieldErrors.contractType}
										</div>
									)}
								</FormControl>
								<FormControl sx={{ flex: 1 }} disabled={!isHubspotDealSelected} error={Boolean(fieldErrors.adjustmentLocation)}>
									<InputLabel id="postAmendmentAdjustmentLocationLabel">Adjustment Location</InputLabel>
									<Select
										labelId="postAmendmentAdjustmentLocationLabel"
										value={formData.adjustmentLocation}
										label="Adjustment Location"
										onChange={handleAdjustmentLocationChange}
									>
										<MenuItem value="existing_phase">Add to Selected Existing Phase</MenuItem>
										<MenuItem value="amendment_phase">Amendment Phase</MenuItem>
									</Select>
									{fieldErrors.adjustmentLocation && (
										<div className="postAmendmentFieldError">
											{fieldErrors.adjustmentLocation}
										</div>
									)}
								</FormControl>
							</div>
							{showContractTypeMismatch && (
								<Alert severity="warning" sx={{ mt: 1, alignSelf: "stretch" }}>
									The selected existing BQE phase has a contract type that doesn't match your selection.
								</Alert>
							)}
							{!showContractTypeMismatch && adjustmentInfoMessage && (
								<Alert severity="info" sx={{ mt: 1, alignSelf: "stretch" }}>
									{adjustmentInfoMessage}
								</Alert>
							)}
						</div>
					<div className="postAmendmentCard">
						<div className="postAmendmentCardHeader">
							<div className="postAmendmentScopeTitle">
								<span>Scope of Service(s)</span>
							</div>
							<ButtonGroup>
								<Button
									variant="outlined"
									color="error"
									onClick={removeServicePressed}
									disabled={!isHubspotDealSelected}
								>
									Remove
								</Button>
								<Button
									variant="contained"
									onClick={addServicePressed}
									disabled={!isHubspotDealSelected}
								>
									Add
								</Button>
							</ButtonGroup>
						</div>
						<div className="postAmendmentServiceTextFields">
							{serviceRows.map(({ rowNumber, scopeKey, costKey }) => (
								<div
									className="postAmendmentServiceTextFieldsRow"
									key={scopeKey}
								>
										<TextField
											sx={{ flex: 5 }}
											label={`Service ${rowNumber}`}
											name={scopeKey}
											value={serviceScopes[scopeKey] || ""}
											onChange={serviceScopeChanged}
											disabled={!isHubspotDealSelected}
											error={Boolean(fieldErrors[scopeKey])}
											helperText={fieldErrors[scopeKey] || ""}
										/>
										<TextField
											sx={{ flex: 1 }}
											label="Estimated Cost"
											name={costKey}
											value={serviceScopeCosts[costKey] || ""}
											onChange={serviceScopeCostChanged}
											disabled={!isHubspotDealSelected}
											error={Boolean(fieldErrors[costKey])}
											helperText={fieldErrors[costKey] || ""}
											InputProps={{
												startAdornment: (
													<InputAdornment position="start">
														$
												</InputAdornment>
											),
										}}
									/>
								</div>
							))}
						</div>
						<div className="postAmendmentScopeMobileNote">
							The information you put here will be included on the
							client facing document <em>exactly</em> as written.
						</div>
					</div>
					</div>
					<div className="postAmendmentActions">
						{alertMessage && (
							<Alert
								sx={{ flex: 1, marginRight: "auto" }}
								severity={alertType}
								onClose={() => setAlertMessage("")}
							>
								{alertMessage}
							</Alert>
						)}
						<div className="postAmendmentButtonGroup">
							<Button
								variant="contained"
								onClick={handleSubmit}
								disabled={isSubmitting || showContractTypeMismatch}
							>
								Submit
							</Button>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default PostAmendment;
